/**
 * NMS Comfort Coins - PDP Earn Preview
 *
 * Displays an estimated "Earn X Comfort Coins on your purchase" message based on:
 *   selected variant selling price (minor units) x tier multiplier
 *
 * - Guests: uses the block-configured guest multiplier. No API request is made.
 * - Logged-in customers: calls the existing Laravel loyalty endpoint with the Shopify
 *   customer ID to resolve the normalized tier. Expected response shape:
 *     { "status": "success", "data": { "tier": "ambassador", "available_coins": 6089.23 } }
 *   The tier handle is matched against the merchant-configured tier map (block setting,
 *   one "tier_handle:multiplier" per line, e.g. "ambassador:2.5"). Unknown tiers or
 *   request failures fall back to the default multiplier. The actual loyalty
 *   calculation/crediting is entirely managed by Nector - this is a display-only estimate.
 *
 * Text placeholders: [points] and [multiplier] (square brackets - double braces would
 * collide with Shopify's dynamic source syntax in text settings).
 *
 * Rounding strategy: points are rounded to the nearest whole coin (Math.round, i.e.
 * round-half-up). The theme has no pre-existing loyalty rounding convention, so this is
 * intentionally isolated in #calculatePoints so it can be revisited later (e.g. floor,
 * or round to the nearest 5) without touching the rest of the component.
 */

class NMSComfortCoins extends window.NMSBaseClass {
  // Shared across all instances on the page so that:
  //  - multiple Comfort Coins blocks reuse the same resolved tier
  //  - Theme Editor section reloads don't trigger duplicate API calls
  static tierCache = new Map(); // customerId -> resolved tier string (or null)
  static pendingRequests = new Map(); // customerId -> in-flight Promise<tier>

  // Same backend domain already used by the EDD (delivery date) components
  // (see nms-check-delivery-date.js / nms-cart-delivery-date.js).
  static API_URL = 'https://pos.neemans.com/api/loyalty/customer';

  constructor(element) {
    super();
    this.element = element;
    this.blockId = element.dataset.blockId || `comfort-coins-${Date.now()}`;

    window.neemans.comfortCoins = window.neemans.comfortCoins || {};
    window.neemans.comfortCoins[this.blockId] = this;

    this.formId = element.dataset.formId;
    this.textTemplate = element.dataset.textTemplate || '';
    this.isLoggedIn = element.dataset.customerLoggedIn === 'true';
    this.customerId = element.dataset.customerId || '';

    this.guestMultiplier = this.#toNumber(element.dataset.guestMultiplier, 5);
    this.defaultMultiplier = this.#toNumber(element.dataset.defaultMultiplier, 5);
    this.tierMultipliers = this.#parseTierMultipliers(element.dataset.tierMultipliers);

    this.defaultRedeemPoints = this.#toNumber(element.dataset.defaultRedeemPoints, 1000);
    this.redeemPointsByTier = this.#parseTierMultipliers(element.dataset.redeemPointsByTier);
    this.currentRedeemPoints = this.defaultRedeemPoints;

    this.currentPriceCents = parseInt(element.dataset.variantPrice, 10) || 0;
    this.currentMultiplier = this.isLoggedIn ? this.defaultMultiplier : this.guestMultiplier;

    this.textElement = element.querySelector('[data-nms-comfort-coins-text]');
    this.templateElements = element.querySelectorAll('[data-cc-template]');
    this.abortController = new AbortController();
  }

  #toNumber(value, fallback) {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  /**
   * Parses the merchant-configured tier map into an object.
   * Accepts one "tier_handle:multiplier" pair per line (commas also supported),
   * e.g. "entry:1\ninsider:1.5\nambassador:2.5". Handles are lowercased so they
   * compare directly against the API's normalized tier value.
   */
  #parseTierMultipliers(raw) {
    const map = {};
    if (!raw) return map;

    for (const pair of raw.split(/[\n,]+/)) {
      const [handle, multiplier] = pair.split(':').map(part => part && part.trim());
      const parsed = parseFloat(multiplier);

      if (handle && Number.isFinite(parsed)) {
        map[handle.toLowerCase()] = parsed;
      }
    }

    return map;
  }

  init() {
    this.#render();
    this.#bindVariantChange();

    // Only logged-in customers require a network request. Guests never call the API.
    if (this.isLoggedIn && this.customerId) {
      this.#resolveTier();
    }
  }

  #bindVariantChange() {
    if (!this.formId) return;

    const form = document.getElementById(this.formId);
    if (!form) return;

    // Reuses the theme's existing variant pub/sub: the product form dispatches
    // "variant:change" (see theme.js VariantPicker#selectCombination) whenever the
    // shopper selects a different variant. We only recalculate here - no new tier
    // request is triggered by a variant change.
    form.addEventListener('variant:change', event => this.#onVariantChange(event), {
      signal: this.abortController.signal,
    });
  }

  #onVariantChange(event) {
    const variant = event.detail && event.detail.variant;
    if (!variant || typeof variant.price !== 'number') return;

    this.currentPriceCents = variant.price;
    this.#render();
  }

  async #resolveTier() {
    const cacheKey = String(this.customerId);

    if (NMSComfortCoins.tierCache.has(cacheKey)) {
      this.#applyTier(NMSComfortCoins.tierCache.get(cacheKey));
      return;
    }

    let requestPromise = NMSComfortCoins.pendingRequests.get(cacheKey);

    if (!requestPromise) {
      requestPromise = this.#fetchTier(cacheKey);
      NMSComfortCoins.pendingRequests.set(cacheKey, requestPromise);
    }

    const tier = await requestPromise;
    this.#applyTier(tier);
  }

  async #fetchTier(cacheKey) {
    try {
      const url = `${NMSComfortCoins.API_URL}?customer_id=${encodeURIComponent(cacheKey)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const payload = await response.json();
      const tier = (payload && payload.data && payload.data.tier ? String(payload.data.tier) : '').toLowerCase() || null;

      NMSComfortCoins.tierCache.set(cacheKey, tier);
      return tier;
    } catch (error) {
      // Loyalty failures must never break the PDP - fall back silently to the default multiplier.
      console.warn('[NMS Comfort Coins] Failed to resolve loyalty tier:', error);
      NMSComfortCoins.tierCache.set(cacheKey, null);
      return null;
    } finally {
      NMSComfortCoins.pendingRequests.delete(cacheKey);
    }
  }

  #applyTier(tier) {
    // Safe fallback for missing/unrecognized tiers: use the configured default multiplier.
    this.currentMultiplier = (tier && this.tierMultipliers[tier] != null)
      ? this.tierMultipliers[tier]
      : this.defaultMultiplier;

    // Redeem points per tier: use the mapped value, or the default fallback.
    this.currentRedeemPoints = (tier && this.redeemPointsByTier[tier] != null)
      ? this.redeemPointsByTier[tier]
      : this.defaultRedeemPoints;

    this.#render();
  }

  // Isolated calculation: selected variant price (minor units) x multiplier (%).
  // Merchant enters multipliers as whole numbers (5 = 5%), so we divide by 100.
  #calculatePoints(priceCents, multiplier) {
    const majorUnits = priceCents / 100;
    return Math.floor(majorUnits * (multiplier / 100));
  }

  #render() {
    if (!this.textElement) return;

    const points = this.#calculatePoints(this.currentPriceCents, this.currentMultiplier);

    const text = this.textTemplate
      .replace(/\[\s*points\s*\]/g, points)
      .replace(/\[\s*multiplier\s*\]/g, this.currentMultiplier)
      .replace(/\[\s*redeem_points\s*\]/g, this.currentRedeemPoints);

    this.textElement.textContent = text;

    if (this.templateElements) {
      for (const el of this.templateElements) {
        const template = el.dataset.ccTemplate || '';
        const rendered = template
          .replace(/\[\s*points\s*\]/g, points)
          .replace(/\[\s*multiplier\s*\]/g, this.currentMultiplier)
          .replace(/\[\s*redeem_points\s*\]/g, this.currentRedeemPoints);

        // modal_content is richtext (HTML) — use innerHTML; others are plain text
        if (el.classList.contains('nms-comfort-coins__modal-content')) {
          el.innerHTML = rendered;
        } else {
          el.textContent = rendered;
        }
      }
    }
  }

  destroy() {
    this.abortController.abort();

    if (window.neemans.comfortCoins && window.neemans.comfortCoins[this.blockId]) {
      delete window.neemans.comfortCoins[this.blockId];
    }

    super.destroy();
  }
}

/**
 * Auto-initialize all Comfort Coins blocks within a container.
 * Guards against duplicate initialization via the data-initialized attribute,
 * matching the theme's existing NMS component lifecycle pattern.
 */
function initNMSComfortCoins(container = document) {
  if (!window.NMSBaseClass) {
    console.error('NMSComfortCoins: NMSBaseClass required');
    return;
  }

  const elements = container.querySelectorAll('[data-nms-comfort-coins]:not([data-initialized])');

  for (const element of elements) {
    const instance = new NMSComfortCoins(element);
    instance.init();
    element.setAttribute('data-initialized', 'true');
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initNMSComfortCoins());
} else {
  initNMSComfortCoins();
}

// Theme Editor: re-initialize only the reloaded section's markup. The static
// tier cache/pending-request map is preserved across reloads, so no duplicate
// loyalty API calls happen for the same customer.
document.addEventListener('shopify:section:load', event => {
  if (event.target.querySelector('[data-nms-comfort-coins]')) {
    initNMSComfortCoins(event.target);
  }
});

window.neemans = window.neemans || {};
window.neemans.NMSComfortCoins = NMSComfortCoins;
